import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useReminders, useReminderOccurrences, repeatRuleMatchesDay, getRepeatTimeForDay, getRepeatGraceMinutes, type Reminder } from "@/hooks/useReminders";
import { formatDistanceToNow, differenceInCalendarDays, isSameWeek, format, parseISO, isPast, isToday, getISODay } from "date-fns";
import { nb } from "date-fns/locale";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bell, Trash2, Backpack, CalendarDays, Clock, MessageCircle, ClipboardList, Plus, Eye, EyeOff, CheckCircle2, Archive, RotateCcw, Pencil, Repeat, AlertTriangle } from "lucide-react";
import { useRef, useState, useCallback, useMemo, useEffect } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { formatChildFriendlyDate } from "@/lib/family-date";
import { cn } from "@/lib/utils";
import { EditReminderSheet } from "@/components/EditReminderSheet";
import { ReminderDetailSheet } from "@/components/ReminderDetailSheet";
import type { LucideIcon } from "lucide-react";
import type { DeviceHashes } from "@/hooks/useDeviceHashes";

interface Props {
  childId: string;
  isParent?: boolean;
  onAdd?: () => void;
  /** When set, child view filters to only this date + overdue */
  date?: string;
  /** Device hashes for child device access (bypasses RLS via RPCs) */
  deviceHashes?: DeviceHashes | null;
  /** When true, no mutations fire (auto-seen, confirm, etc.) — used for parent preview */
  readOnly?: boolean;
  /** Når true (f.eks. parent med sticky header): ikke vis seksjonsheader */
  hideHeader?: boolean;
  /** Når true: header rendres men er usynlig (beholder plass, unngår flicker ved scroll) */
  headerInvisible?: boolean;
}

/** MVP: Only Beskjed / Ta med / Frist. Legacy action/event are rendered as Beskjed (see effectiveReminderCategory). */
const CATEGORY_ICONS: Record<string, LucideIcon> = {
  take_along: Backpack,
  deadline: Clock,
  info: MessageCircle,
};

const CATEGORY_LABELS: Record<string, string> = {
  take_along: "Ta med",
  deadline: "Frist",
  info: "Beskjed",
};

const CATEGORY_EMOJI: Record<string, string> = {
  take_along: "🎒",
  deadline: "⏰",
  info: "💬",
};

const DEFAULT_ICON = ClipboardList;

const SWIPE_THRESHOLD = 80;

function smartDateLabel(dateStr: string): { label: string; color: string } {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const d = parseISO(dateStr);
  const offset = differenceInCalendarDays(d, now);
  if (offset < 0) return { label: offset === -1 ? "I går" : format(d, "d. MMM", { locale: nb }), color: "text-destructive border-destructive/30" };
  if (offset === 0) return { label: "I dag", color: "text-primary border-primary/30 bg-primary/10" };
  if (offset === 1) return { label: "I morgen", color: "text-amber-600 dark:text-amber-400 border-amber-400/30" };
  if (isSameWeek(d, now, { weekStartsOn: 1 })) return { label: format(d, "EEEE", { locale: nb }).replace(/^\w/, c => c.toUpperCase()), color: "text-muted-foreground border-muted-foreground/30" };
  return { label: format(d, "d. MMM", { locale: nb }), color: "text-muted-foreground border-muted-foreground/30" };
}

/** Returns a time chip label + color for reminders with a specific time */
function smartTimeLabel(timeStr: string, dateStr: string): { label: string; color: string } | null {
  // Build a full datetime to check if past
  const d = parseISO(`${dateStr}T${timeStr}:00`);
  if (isPast(d)) {
    return { label: timeStr, color: "text-destructive border-destructive/30" };
  }
  return { label: timeStr, color: "text-muted-foreground border-muted-foreground/30" };
}

/** Legacy: time label from remind_at ISO string */
function smartTimeLabelFromISO(remindAt: string): { label: string; color: string } | null {
  const d = parseISO(remindAt);
  const timeStr = format(d, "HH:mm");
  if (isPast(d)) {
    return { label: timeStr, color: "text-destructive border-destructive/30" };
  }
  return { label: timeStr, color: "text-muted-foreground border-muted-foreground/30" };
}

/** Sort key: use remind_at timestamp if available, else remind_date, else push to end */
function reminderSortKey(r: Reminder, date?: string): string {
  if (r.remind_at) return r.remind_at;
  // For repeating reminders, use today's scheduled time if available
  if (r.repeat_rule && date) {
    const isoDay = getISODay(parseISO(date));
    const time = getRepeatTimeForDay(r.repeat_rule, isoDay);
    if (time) return `${date}T${time}:00`;
  }
  if (r.remind_date) return r.remind_date + "T23:59:59";
  return "9999";
}

/** Categories that show confirm/done-style UI. MVP: only take_along and deadline (not action; action is legacy → Beskjed). */
const ACTION_CATEGORIES = new Set(["take_along", "deadline"]);

/** Effective seen/confirmed status — uses occurrence for repeating reminders */
interface EffectiveStatus {
  seen_by_child_at: string | null;
  confirmed_at: string | null;
  isRepeating: boolean;
  /** Scheduled time for today (from repeat_rule) */
  scheduledTime: string | null;
  /** Whether this reminder is overdue (past scheduled time + grace) */
  isOverdue: boolean;
  /** Grouping bucket for sectioning */
  group: "overdue" | "today" | "coming";
}

/** Child-friendly reminder card — differentiates tasks from messages */
function ChildReminderCard({
  reminder,
  status,
  onConfirm,
  todayDate,
  registerRef,
}: {
  reminder: Reminder;
  status: EffectiveStatus;
  onConfirm: (id: string) => void;
  /** YYYY-MM-DD string for "today" */
  todayDate?: string;
  /** Callback to register DOM ref for IntersectionObserver auto-seen */
  registerRef?: (id: string, el: HTMLDivElement | null) => void;
}) {
  const isSeen = !!status.seen_by_child_at;
  const isConfirmed = !!status.confirmed_at;
  const effectiveCategory = reminder.category === "action" || reminder.category === "event" ? "info" : (reminder.category || "info");
  const emoji = CATEGORY_EMOJI[effectiveCategory] || "📋";
  const isActionable = ACTION_CATEGORIES.has(effectiveCategory);

  const futureDate = useMemo(() => {
    if (!reminder.remind_date) return null;
    if (todayDate && reminder.remind_date <= todayDate) return null;
    return formatChildFriendlyDate(reminder.remind_date);
  }, [reminder.remind_date, todayDate]);

  const timeChip = useMemo(() => {
    if (status.scheduledTime && todayDate) {
      return smartTimeLabel(status.scheduledTime, todayDate);
    }
    return null;
  }, [status.scheduledTime, todayDate]);

  return (
    <div
      ref={(el) => registerRef?.(reminder.id, el)}
      data-reminder-id={reminder.id}
    >
      <Card className={`transition-all ${
        isConfirmed
          ? "opacity-60"
          : isSeen && !isActionable
            ? "opacity-60"
            : isActionable
              ? "border-primary/20 bg-primary/5"
              : "border-accent/20 bg-accent/5"
      }`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="text-2xl shrink-0 mt-0.5">{emoji}</div>
            <div className="flex-1 min-w-0 space-y-1.5">
              <p className="font-semibold">{reminder.title}</p>

              <div className="flex items-center gap-1.5 flex-wrap">
                {futureDate && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-muted-foreground/30">
                    📅 {futureDate}
                  </Badge>
                )}
                {timeChip && (
                  <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 flex items-center gap-1", timeChip.color)}>
                    <Clock className="h-3 w-3" /> {timeChip.label}
                  </Badge>
                )}
              </div>

              {/* Action area */}
              {/* Actionable: show "Gjort!" button until confirmed */}
              {isActionable && !isConfirmed && (
                <Button
                  size="sm"
                  className="rounded-xl mt-2 font-bold w-full py-3 bg-primary text-primary-foreground hover:bg-primary/90"
                  onClick={() => onConfirm(reminder.id)}
                >
                  ✅ Gjort!
                </Button>
              )}
              {/* Non-actionable: show button only if not yet auto-seen */}
              {!isActionable && !isSeen && !isConfirmed && (
                <Button
                  size="sm"
                  className="rounded-xl mt-2 font-bold w-full py-3 bg-accent text-accent-foreground hover:bg-accent/90"
                  onClick={() => onConfirm(reminder.id)}
                >
                  👍 Jeg har sett det!
                </Button>
              )}
              {/* Status indicators */}
              {isSeen && !isConfirmed && !isActionable && (
                <div className="flex items-center gap-1.5 mt-1 text-muted-foreground">
                  <span className="text-base">👀</span>
                  <span className="text-xs font-medium">Du har sett denne</span>
                </div>
              )}
              {isConfirmed && (
                <div className="flex items-center gap-1.5 mt-1 text-muted-foreground">
                  <span className="text-base">✅</span>
                  <span className="text-xs font-medium">{isActionable ? "Utført" : "Bekreftet!"}</span>
                </div>
              )}
              {isSeen && !isConfirmed && isActionable && (
                <div className="flex items-center gap-0.5 mt-0.5 text-muted-foreground">
                  <span className="text-[10px]">👀 Sett</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/** Parent swipeable card with archive/delete actions */
function SwipeableCard({
  reminder,
  status,
  isParent,
  onDismiss,
  onDelete,
  onTap,
  date,
}: {
  reminder: Reminder;
  status: EffectiveStatus;
  isParent: boolean;
  onDismiss: (id: string) => void;
  onDelete: (id: string) => void;
  onTap: (id: string) => void;
  date?: string;
}) {
  const startXRef = useRef(0);
  const currentXRef = useRef(0);
  const [offsetX, setOffsetX] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const isDragging = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startXRef.current = e.touches[0].clientX;
    currentXRef.current = 0;
    isDragging.current = false;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const delta = e.touches[0].clientX - startXRef.current;
    currentXRef.current = delta;
    if (Math.abs(delta) > 10) isDragging.current = true;
    if (revealed) {
      const newOffset = Math.min(0, -140 + delta);
      setOffsetX(newOffset);
    } else {
      const clamped = Math.min(0, delta);
      setOffsetX(clamped);
    }
  }, [revealed]);

  const handleTouchEnd = useCallback(() => {
    const delta = currentXRef.current;
    if (revealed) {
      if (delta > 40) {
        setRevealed(false);
        setOffsetX(0);
      } else {
        setOffsetX(-140);
      }
    } else {
      if (delta < -SWIPE_THRESHOLD) {
        setRevealed(true);
        setOffsetX(-140);
      } else {
        setOffsetX(0);
      }
    }
    setTimeout(() => { isDragging.current = false; }, 0);
  }, [revealed]);

  const handleClick = useCallback(() => {
    if (isDragging.current) return;
    if (revealed) {
      setRevealed(false);
      setOffsetX(0);
      return;
    }
    onTap(reminder.id);
  }, [revealed, onTap, reminder.id]);

  const effectiveCategory = reminder.category === "action" || reminder.category === "event" ? "info" : (reminder.category || "info");
  const IconComponent = CATEGORY_ICONS[effectiveCategory] || DEFAULT_ICON;
  const label = CATEGORY_LABELS[effectiveCategory] || null;
  const isSeen = !!status.seen_by_child_at;
  const isConfirmed = !!status.confirmed_at;

  return (
    <div className="relative overflow-hidden rounded-xl">
      {/* Background actions */}
      <div className="absolute inset-y-0 right-0 flex h-full">
        <button
          className="flex w-[70px] items-center justify-center bg-primary text-primary-foreground"
          onClick={() => onDismiss(reminder.id)}
        >
          <div className="flex flex-col items-center gap-0.5">
            <Archive className="h-4 w-4" />
            <span className="text-[10px] font-medium">Arkiver</span>
          </div>
        </button>
        <button
          className="flex w-[70px] items-center justify-center bg-destructive text-destructive-foreground"
          onClick={() => onDelete(reminder.id)}
        >
          <div className="flex flex-col items-center gap-0.5">
            <Trash2 className="h-4 w-4" />
            <span className="text-[10px] font-medium">Slett</span>
          </div>
        </button>
      </div>

      {/* Foreground card */}
      <div
        className="relative bg-background transition-transform duration-200 ease-out"
        style={{ transform: `translateX(${offsetX}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleClick}
      >
        <Card className="border shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-lg p-1.5 bg-secondary">
                <IconComponent className={cn("h-4 w-4", status.isOverdue ? "text-destructive" : "text-primary")} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium flex-1">{reminder.title}</p>
                  {/* Compact status icon (rightmost) */}
                  {isParent && (
                    <div className="shrink-0">
                      {isConfirmed ? (
                        <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />
                      ) : isSeen ? (
                        <Eye className="h-4 w-4 text-primary" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-muted-foreground/50" />
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  {/* Overdue label */}
                  {status.isOverdue && status.scheduledTime && (
                    <Badge variant="destructive" className="text-[10px] px-1.5 py-0 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Forfalt kl {status.scheduledTime}
                    </Badge>
                  )}
                  {status.isOverdue && !status.scheduledTime && (
                    <Badge variant="destructive" className="text-[10px] px-1.5 py-0 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Forfalt
                    </Badge>
                  )}
                  {/* Category chip */}
                  {label && !status.isOverdue && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 flex items-center gap-1">
                      <IconComponent className="h-3 w-3" /> {label}
                    </Badge>
                  )}
                  {/* Date chip (only for non-today dates) */}
                  {reminder.remind_date && !status.isOverdue && (() => {
                    const { label: dateLabel, color } = smartDateLabel(reminder.remind_date);
                    return (
                      <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 flex items-center gap-1", color)}>
                        <CalendarDays className="h-3 w-3" /> {dateLabel}
                      </Badge>
                    );
                  })()}
                  {/* Time chip (only when not overdue — overdue already shows time) */}
                  {!status.isOverdue && reminder.remind_at && (() => {
                    const timeInfo = smartTimeLabelFromISO(reminder.remind_at);
                    if (!timeInfo) return null;
                    return (
                      <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 flex items-center gap-1", timeInfo.color)}>
                        <Clock className="h-3 w-3" /> {timeInfo.label}
                      </Badge>
                    );
                  })()}
                  {!status.isOverdue && !reminder.remind_at && status.scheduledTime && date && (() => {
                    const timeInfo = smartTimeLabel(status.scheduledTime, date);
                    if (!timeInfo) return null;
                    return (
                      <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 flex items-center gap-1", timeInfo.color)}>
                        <Clock className="h-3 w-3" /> {timeInfo.label}
                      </Badge>
                    );
                  })()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function ReminderSection({ childId, isParent = false, onAdd, date, deviceHashes, readOnly = false, hideHeader = false, headerInvisible = false }: Props) {
  const { items, archivedItems, isLoading, dismissReminder, deleteReminder, markSeen, restoreReminder } = useReminders(childId, deviceHashes);
  const { occurrenceMap, markOccurrenceSeen, confirmReminder, markSeenDevice, confirmReminderDevice } = useReminderOccurrences(childId, date, deviceHashes);

  /** Har barnet noen påminnelser i DB (alle tiders)? Aktive brukere får "Ingen påminnelser" i stedet for eksempelkort. */
  const { data: hasAnyRemindersInDb } = useQuery({
    queryKey: ["child-has-any-reminders", childId],
    queryFn: async () => {
      if (!childId) return false;
      const { data, error } = await supabase
        .from("reminders")
        .select("id")
        .eq("child_id", childId)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return !!data;
    },
    enabled: !!childId && isParent,
  });
  const [seenOpen, setSeenOpen] = useState(false);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<Reminder | null>(null);
  const [detailTarget, setDetailTarget] = useState<Reminder | null>(null);

  // Auto-seen tracking: mark reminders as seen after 3s of visibility
  const seenTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const cardRefsRef = useRef<Map<string, HTMLDivElement>>(new Map());

  const handleAutoSeen = useCallback((reminderId: string) => {
    if (!date || readOnly) return;
    const r = items.find((i) => i.id === reminderId);
    if (!r) return;
    const status = getEffectiveStatusFn(r);
    if (status.seen_by_child_at || status.confirmed_at) return; // already seen/confirmed

    if (deviceHashes) {
      // Device path: use RPC for both repeating and non-repeating
      markSeenDevice.mutate({ reminderId, occurrenceDate: date });
    } else if (r.repeat_rule) {
      markOccurrenceSeen.mutate({ reminderId, occurrenceDate: date });
    } else {
      markSeen.mutate(reminderId);
    }
  }, [items, date, deviceHashes, markSeenDevice, markOccurrenceSeen, markSeen]);

  // Setup IntersectionObserver for child mode auto-seen
  useEffect(() => {
    if (isParent || readOnly) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.getAttribute("data-reminder-id");
          if (!id) continue;
          if (entry.isIntersecting) {
            if (!seenTimersRef.current.has(id)) {
              const timer = setTimeout(() => {
                handleAutoSeen(id);
                seenTimersRef.current.delete(id);
              }, 3000);
              seenTimersRef.current.set(id, timer);
            }
          } else {
            const timer = seenTimersRef.current.get(id);
            if (timer) {
              clearTimeout(timer);
              seenTimersRef.current.delete(id);
            }
          }
        }
      },
      { threshold: 0.5 }
    );

    // Observe all registered cards
    for (const [, el] of cardRefsRef.current) {
      observerRef.current.observe(el);
    }

    return () => {
      observerRef.current?.disconnect();
      for (const timer of seenTimersRef.current.values()) clearTimeout(timer);
      seenTimersRef.current.clear();
    };
  }, [isParent, handleAutoSeen]);

  const registerCardRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) {
      cardRefsRef.current.set(id, el);
      observerRef.current?.observe(el);
    } else {
      const existing = cardRefsRef.current.get(id);
      if (existing) observerRef.current?.unobserve(existing);
      cardRefsRef.current.delete(id);
    }
  }, []);

  /** Build effective status for a reminder, considering occurrences for repeating ones */
  const getEffectiveStatusFn = useCallback((r: Reminder): EffectiveStatus => {
    const isRepeating = !!r.repeat_rule;
    const now = new Date();
    const todayStr = date || "";

    const computeGroup = (isOverdue: boolean, remindDate: string | null): EffectiveStatus["group"] => {
      if (isOverdue) return "overdue";
      if (!remindDate || remindDate === todayStr) return "today";
      if (remindDate > todayStr) return "coming";
      return "overdue"; // past date
    };

    if (isRepeating && date) {
      const isoDay = getISODay(parseISO(date));
      const occ = occurrenceMap.get(r.id);
      const scheduledTime = getRepeatTimeForDay(r.repeat_rule, isoDay);
      const seenAt = occ?.seen_by_child_at ?? null;
      const confirmedAt = occ?.confirmed_at ?? null;
      let isOverdue = false;
      if (!seenAt && !confirmedAt && scheduledTime) {
        const grace = getRepeatGraceMinutes(r.repeat_rule);
        const [h, m] = scheduledTime.split(":").map(Number);
        const deadline = new Date();
        deadline.setHours(h, m + grace, 0, 0);
        isOverdue = now > deadline;
      }
      return { seen_by_child_at: seenAt, confirmed_at: confirmedAt, isRepeating: true, scheduledTime, isOverdue, group: isOverdue ? "overdue" : "today" };
    }
    const isOverdue = !r.seen_by_child_at && !r.confirmed_at && !!r.remind_date && r.remind_date < todayStr;
    const group = computeGroup(isOverdue, r.remind_date);
    return { seen_by_child_at: r.seen_by_child_at, confirmed_at: r.confirmed_at, isRepeating: false, scheduledTime: null, isOverdue, group };
  }, [occurrenceMap, date]);

  // Keep old name for downstream usage
  const getEffectiveStatus = getEffectiveStatusFn;

  // Filter: child mode only shows relevant reminders for the date
  const filtered = useMemo(() => {
    if (isParent || !date) return items;
    const now = new Date();
    const isoDay = getISODay(parseISO(date));
    return items.filter((r) => {
      if (r.repeat_rule) {
        if (!repeatRuleMatchesDay(r.repeat_rule, isoDay)) return false;
        const scheduledTime = getRepeatTimeForDay(r.repeat_rule, isoDay);
        if (scheduledTime) {
          const remindTime = parseISO(`${date}T${scheduledTime}:00`);
          const thirtyMinBefore = new Date(remindTime.getTime() - 30 * 60 * 1000);
          if (now < thirtyMinBefore) return false;
        }
        return true;
      }
      if (!r.remind_date) return true;
      if (r.remind_date > date) return false;
      if (r.remind_at && r.remind_date === date) {
        const remindTime = parseISO(r.remind_at);
        const thirtyMinBefore = new Date(remindTime.getTime() - 30 * 60 * 1000);
        if (now < thirtyMinBefore) return false;
      }
      return true;
    });
  }, [items, isParent, date]);

  const { overdue, today: todayGroup, coming, seen } = useMemo(() => {
    const overdue: Reminder[] = [];
    const today: Reminder[] = [];
    const coming: Reminder[] = [];
    const seen: Reminder[] = [];
    for (const r of filtered) {
      const status = getEffectiveStatus(r);
      // Only move to "seen" bucket when confirmed (utført) by the child.
      // Merely "seen" reminders stay visible so the child can still act on them.
      const isDone = !!status.confirmed_at;
      if (isDone) {
        seen.push(r);
      } else {
        switch (status.group) {
          case "overdue": overdue.push(r); break;
          case "coming": coming.push(r); break;
          default: today.push(r); break;
        }
      }
    }
    const sortByDateTime = (a: Reminder, b: Reminder) => {
      const ka = reminderSortKey(a, date);
      const kb = reminderSortKey(b, date);
      return ka.localeCompare(kb);
    };
    overdue.sort(sortByDateTime);
    today.sort(sortByDateTime);
    coming.sort(sortByDateTime);
    seen.sort(sortByDateTime);
    return { overdue, today, coming, seen };
  }, [filtered, getEffectiveStatus, date, isParent]);

  const active = useMemo(() => [...overdue, ...todayGroup, ...coming], [overdue, todayGroup, coming]);

  if (isLoading) return null;

  if (filtered.length === 0 && archivedItems.length === 0) {
    if (isParent) {
      const placeholders = [
        { Icon: Backpack, title: "F.eks. Husk gymtøy til trening" },
        { Icon: MessageCircle, title: "F.eks. Ta med pakkeliste til bytte" },
      ];
      return (
        <div className="space-y-3">
          {!headerInvisible && (
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-display font-semibold flex items-center gap-2">
                <Bell className="h-5 w-5 text-primary" />
                Påminnelser
                <Badge variant="secondary" className="text-xs">
                  {hasAnyRemindersInDb === false ? placeholders.length : 0}
                </Badge>
              </h2>
              {onAdd && (
                <Button variant="outline" size="sm" className="rounded-full text-xs gap-1 border-primary/40" onClick={onAdd}>
                  <Plus className="h-3.5 w-3.5 text-primary" /> Legg til påminnelse
                </Button>
              )}
            </div>
          )}
          {hasAnyRemindersInDb !== false ? (
            <p className="text-sm text-muted-foreground">Ingen påminnelser.</p>
          ) : (
            <>
              <div className="space-y-3">
                {placeholders.map(({ Icon, title }, idx) => (
                  <Card key={idx} className="border-dashed opacity-75 pointer-events-none border shadow-sm">
                    <CardContent className="p-3">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 rounded-lg p-1.5 bg-secondary">
                          <Icon className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-muted-foreground">{title}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <p className="text-xs text-muted-foreground">Legg til via Legg til-menyen over.</p>
              </div>
            </>
          )}
        </div>
      );
    }
    return null;
  }

  /** Child taps to confirm/acknowledge a reminder */
  const handleConfirm = (id: string) => {
    if (!date || readOnly) return;
    const r = items.find((i) => i.id === id);
    if (!r) return;
    if (deviceHashes) {
      confirmReminderDevice.mutate({ reminderId: id, occurrenceDate: date });
    } else if (r.repeat_rule) {
      confirmReminder.mutate({ reminderId: id, occurrenceDate: date });
    } else {
      // Non-repeating, non-device: update reminder directly
      confirmReminder.mutate({ reminderId: id, occurrenceDate: date });
    }
  };

  /** Parent taps to view detail */
  const handleTap = (id: string) => {
    if (isParent) {
      const r = items.find((i) => i.id === id) ?? archivedItems.find((i) => i.id === id);
      if (r) setDetailTarget(r);
    } else {
      // Child tap = confirm action
      handleConfirm(id);
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteTarget) {
      deleteReminder.mutate(deleteTarget);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-3">
      {!hideHeader && !headerInvisible && (
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-display font-semibold flex items-center gap-2">
            {isParent ? <Bell className="h-5 w-5 text-primary" /> : <span className="text-xl">🔔</span>}
            Påminnelser
            {(filtered.length > 0 || archivedItems.length > 0) && (
              <Badge variant="secondary" className="text-xs">{filtered.length + archivedItems.length}</Badge>
            )}
          </h2>
          {onAdd && (
            <Button variant="outline" size="sm" className="rounded-full text-xs gap-1 border-primary/40" onClick={onAdd}>
              <Plus className="h-3.5 w-3.5 text-primary" /> Legg til påminnelse
            </Button>
          )}
        </div>
      )}

      {/* Child view: simple cards with tap-to-confirm */}
      {!isParent && (
        <div className="space-y-3">
          {active.map((r) => (
            <ChildReminderCard key={r.id} reminder={r} status={getEffectiveStatus(r)} onConfirm={handleTap} todayDate={date} registerRef={registerCardRef} />
          ))}
          {seen.length > 0 && (
            <Collapsible open={seenOpen} onOpenChange={setSeenOpen}>
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors w-full py-1">
                  <span>✅</span>
                  <span>{seen.length} utført</span>
                  <div className="flex-1 border-t border-border/50" />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-2 pt-1">
              {seen.map((r) => (
                  <ChildReminderCard key={r.id} reminder={r} status={getEffectiveStatus(r)} onConfirm={handleTap} todayDate={date} registerRef={registerCardRef} />
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      )}

      {/* Parent view: grouped swipeable cards */}
      {isParent && (
        <>
          {overdue.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                <span>Forfalt</span>
                <div className="flex-1 border-t border-border" />
              </div>
              {overdue.map((r) => (
                <SwipeableCard key={r.id} reminder={r} status={getEffectiveStatus(r)} isParent={isParent} onDismiss={(id) => dismissReminder.mutate(id)} onDelete={(id) => setDeleteTarget(id)} onTap={handleTap} date={date} />
              ))}
            </div>
          )}

          {todayGroup.length > 0 && (
            <div className="space-y-3">
              {overdue.length > 0 && (
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" />
                  <span>I dag</span>
                  <div className="flex-1 border-t border-border/50" />
                </div>
              )}
              {todayGroup.map((r) => (
                <SwipeableCard key={r.id} reminder={r} status={getEffectiveStatus(r)} isParent={isParent} onDismiss={(id) => dismissReminder.mutate(id)} onDelete={(id) => setDeleteTarget(id)} onTap={handleTap} date={date} />
              ))}
            </div>
          )}

          {coming.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <CalendarDays className="h-3.5 w-3.5" />
                <span>Kommende</span>
                <div className="flex-1 border-t border-border/50" />
              </div>
              {coming.map((r) => (
                <SwipeableCard key={r.id} reminder={r} status={getEffectiveStatus(r)} isParent={isParent} onDismiss={(id) => dismissReminder.mutate(id)} onDelete={(id) => setDeleteTarget(id)} onTap={handleTap} date={date} />
              ))}
            </div>
          )}

          {seen.length > 0 && (
            <Collapsible open={seenOpen} onOpenChange={setSeenOpen}>
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full py-1">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>{seen.length} utført</span>
                  <div className="flex-1 border-t border-border/50" />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-2 pt-1">
                {seen.map((r) => (
                  <SwipeableCard
                    key={r.id}
                    reminder={r}
                    status={getEffectiveStatus(r)}
                    isParent={isParent}
                    onDismiss={(id) => dismissReminder.mutate(id)}
                    onDelete={(id) => setDeleteTarget(id)}
                    onTap={handleTap}
                    date={date}
                  />
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}
        </>
      )}

      {/* Archived reminders (parent only) */}
      {isParent && archivedItems.length > 0 && (
        <Collapsible open={archivedOpen} onOpenChange={setArchivedOpen}>
          <CollapsibleTrigger asChild>
            <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full py-1">
              <Archive className="h-3.5 w-3.5" />
              <span>{archivedItems.length} arkivert</span>
              <div className="flex-1 border-t border-border/50" />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-2 pt-1">
            {archivedItems.map((r) => (
              <Card key={r.id} className="border border-dashed opacity-70">
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">{r.title}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Arkivert {r.dismissed_at ? formatDistanceToNow(new Date(r.dismissed_at), { addSuffix: true, locale: nb }) : ""}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs gap-1"
                      onClick={() => restoreReminder.mutate(r.id)}
                    >
                      <RotateCcw className="h-3 w-3" />
                      Gjenopprett
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette påminnelse?</AlertDialogTitle>
            <AlertDialogDescription>
              Påminnelsen fjernes permanent og kan ikke gjenopprettes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Detail sheet (parent only) */}
      <ReminderDetailSheet
        open={!!detailTarget}
        onOpenChange={(open) => !open && setDetailTarget(null)}
        reminder={detailTarget}
        status={detailTarget ? getEffectiveStatus(detailTarget) : null}
        onEdit={() => {
          setEditTarget(detailTarget);
          setDetailTarget(null);
        }}
      />

      {/* Edit reminder sheet (parent only) */}
      <EditReminderSheet
        open={!!editTarget}
        onOpenChange={(open) => !open && setEditTarget(null)}
        reminder={editTarget}
      />
    </div>
  );
}
